import { useState } from 'react';

import PropTypes from 'prop-types';

import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import Tag from '#globalComponents/Tag/Tag';

const exactMatch = (tags, term) => tags.find((t) => t.name.toLowerCase() === term.toLowerCase());

/**
 * Create-on-the-fly tag combobox. Selected tags render as removable chips; typing filters the
 * org's tags and — when no exact match exists — offers a "create" row.
 */
const TagMultiSelect = ({
	value = [],
	allTags = [],
	onChange,
	onCreateTag,
	placeholder = 'Tag hinzufügen …',
	disabled = false,
}) => {
	const [search, setSearch] = useState('');
	const [open, setOpen] = useState(false);
	const [creating, setCreating] = useState(false);

	const selectedIds = new Set(value.map((t) => t.id));
	const term = search.trim();
	const suggestions = allTags.filter(
		(t) => !selectedIds.has(t.id) && t.name.toLowerCase().includes(term.toLowerCase()),
	);
	const canCreate = term.length > 0 && !exactMatch(allTags, term);

	const close = () => {
		setOpen(false);
		setSearch('');
	};

	const addTag = (tag) => {
		if (!selectedIds.has(tag.id)) onChange([...value, tag]);
		setSearch('');
	};

	const removeTag = (tagId) => onChange(value.filter((t) => t.id !== tagId));

	const handleCreate = async () => {
		if (!canCreate || creating) return;
		const existing = exactMatch(suggestions, term);
		if (existing) return addTag(existing);
		setCreating(true);
		try {
			const created = await onCreateTag(term);
			if (created) addTag(created);
		} finally {
			setCreating(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			const match = exactMatch(suggestions, term);
			if (match) addTag(match);
			else if (canCreate) void handleCreate();
		} else if (e.key === 'Backspace' && term === '' && value.length > 0) {
			removeTag(value[value.length - 1].id);
		} else if (e.key === 'Escape') {
			close();
		}
	};

	const trigger = (
		<div
			className={`flex flex-wrap items-center gap-1 w-full rounded-card border border-bgBase-500 bg-white shadow-field transition-[border-color,box-shadow] px-3 py-1.5 focus-within:border-primary-300 focus-within:shadow-field-focus ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
		>
			{value.map((tag) => (
				<Tag key={tag.id} label={tag.name} size="xs" onRemove={() => removeTag(tag.id)} />
			))}
			<input
				type="text"
				value={search}
				onChange={(e) => {
					setSearch(e.target.value);
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onKeyDown={handleKeyDown}
				placeholder={value.length === 0 ? placeholder : ''}
				className="flex-1 min-w-[80px] bg-transparent text-sm outline-none py-0.5"
			/>
		</div>
	);

	return (
		<Popover open={open} onClose={close} trigger={trigger} align="stretch">
			<Panel.Body padded={false}>
				<Menu>
					{canCreate && (
						<Menu.Item onClick={handleCreate} disabled={creating}>
							<span className="flex items-center gap-1.5">
								<span className="text-bgBase-800">+</span>
								<span>
									„<span className="font-medium">{term}</span>“ erstellen
								</span>
							</span>
						</Menu.Item>
					)}
					{suggestions.map((tag) => (
						<Menu.Item
							key={tag.id}
							onClick={() => addTag(tag)}
							trailing={typeof tag.usageCount === 'number' ? tag.usageCount : undefined}
						>
							{tag.name}
						</Menu.Item>
					))}
					{suggestions.length === 0 && !canCreate && <Menu.Empty>Keine Tags</Menu.Empty>}
				</Menu>
			</Panel.Body>
		</Popover>
	);
};

const tagShape = PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired });

TagMultiSelect.propTypes = {
	value: PropTypes.arrayOf(tagShape),
	allTags: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	onCreateTag: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	disabled: PropTypes.bool,
};

export default TagMultiSelect;
